import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fetchParentStudents, fetchMessagingSummary } from '../../services/api';
import GradingColorReference from '../../components/GradingColorReference';
import StudentProfileAvatar from '../../components/StudentProfileAvatar';
import './Home.css';
import { useUnreadMessagingSummary } from '../../utils/messageReadState';

const SCHOOL_BRAND_BY_NAME = {
  'Stellar Starlight School': {
    logo: '/69e03c5f0ce260dc5b607ea2_Stellar%20Starlight%20Main%20Logo%20-%20Color.png',
    accent: '#5F9498',
    surface: '#EEF9F4',
  },
  'CALE School': {
    logo: '/cale-logo.png',
    accent: '#40669D',
    surface: '#EEF7FF',
  },
};

const panelStyle = {
  background: '#ffffff',
  border: '1px solid #dbe4f0',
  borderRadius: 18,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
};

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userName = user?.first_name ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}` : (user?.name || user?.email || '');
  const [students, setStudents] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [rawMessagingSummary, setMessagingSummary] = useState<any>(null);
  const messagingSummary = useUnreadMessagingSummary(rawMessagingSummary, user);

  useEffect(() => {
    let mounted = true;
    fetchParentStudents()
      .then((data) => {
        if (mounted) setStudents(data.students || []);
      })
      .catch(() => {
        if (mounted) setStudents([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    if (typeof fetchMessagingSummary === 'function') {
      fetchMessagingSummary()
        .then((data) => {
          if (mounted) setMessagingSummary(data);
        })
        .catch(() => {
          if (mounted) setMessagingSummary(null);
        });
    }

    return () => {
      mounted = false;
    };
  }, []);

  const studentSummaries = useMemo(() => {
    return students.map((student) => ({
      student,
      childName: `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`,
      classes: student.classes || [],
    }));
  }, [students]);

  const enrolledSchools = useMemo(() => {
    const byKey = new Map();

    students.forEach((student) => {
      const classes = Array.isArray(student.classes) ? student.classes : [];

      classes.forEach((cls) => {
        const schoolId = cls?.school_id;
        const schoolName = cls?.school_name || '';
        const key = schoolId != null ? `id:${schoolId}` : `name:${schoolName.toLowerCase()}`;
        if (!schoolName || byKey.has(key)) return;

        const themed = SCHOOL_BRAND_BY_NAME[schoolName] || {};
        byKey.set(key, {
          name: schoolName,
          type: 'Partner School',
          logo: themed.logo || '/academic-eye-logo.png',
          accent: themed.accent || '#40669D',
          surface: themed.surface || '#EEF7FF',
        });
      });

      if (classes.length === 0 && student?.school_name) {
        const schoolName = student.school_name;
        const schoolId = student.school_id;
        const key = schoolId != null ? `id:${schoolId}` : `name:${schoolName.toLowerCase()}`;
        if (byKey.has(key)) return;

        const themed = SCHOOL_BRAND_BY_NAME[schoolName] || {};
        byKey.set(key, {
          name: schoolName,
          type: 'Partner School',
          logo: themed.logo || '/academic-eye-logo.png',
          accent: themed.accent || '#40669D',
          surface: themed.surface || '#EEF7FF',
        });
      }
    });

    return Array.from(byKey.values());
  }, [students]);

  return (
    <div className="family-home">
      <div className="ae-page-hero" style={{ marginBottom: 0 }}>
        <h1>{`Welcome${userName ? ', ' + userName : ''}!`}</h1>
        <p>Use Home, Grading, Action, Connect, and Help to move through your family dashboard the same way each time.</p>
      </div>

      <section className="family-home-overview" aria-labelledby="family-overview-title">
        <h2 id="family-overview-title">Family Dashboard Overview</h2>
        <p>Open Student Profiles for grading and reports, View Data for data points and shared evidence, Action for support cards, Connect for messages, and Help for tutorials. Student Goals: Coming soon.</p>
      </section>

      <section className="family-home-students" aria-labelledby="family-students-title">
        <h2 id="family-students-title">Your Students</h2>
        {loading ? (
          <p>Loading student profiles...</p>
        ) : studentSummaries.length === 0 ? (
          <p>No linked student profiles yet.</p>
        ) : (
          <div className="family-home-student-list">
            {studentSummaries.map(({ student, childName }) => (
              <button
                key={student.student_id}
                type="button"
                className="family-home-student-card"
                onClick={() => navigate('/parent/student-profiles', {
                  state: {
                    selectedStudentId: student.student_id,
                    student,
                    scrollToSnapshot: true,
                    returnTo: { pathname: '/parent/home', label: '← Back to Home' },
                  },
                })}
              >
                <StudentProfileAvatar student={student} className="family-home-student-avatar" />
                <span><strong>{childName}</strong><span className="family-home-student-grade">Grade {student.grade_level ?? 'N/A'}</span></span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="family-home-hubs" aria-label="Family dashboard hubs">
        {[
          { title: 'Grading', desc: 'Student Profiles, added data, and report generation.', path: '/parent/student-profiles' },
          { title: 'Action', desc: 'Guided Actions for next steps. Student Goals: Coming soon.', path: '/parent/action' },
          { title: 'Connect', desc: 'Connect for messages and communication.', path: '/parent/connect' },
          { title: 'Help', desc: 'Tutorials for navigating the system.', path: '/parent/help-support' },
        ].map((card) => (
          <button key={card.title} type="button" className="family-home-hub-card" onClick={() => navigate(card.path)}>
            <span className="family-home-hub-title">{card.title}</span>
            <span className="family-home-hub-accent" aria-hidden="true" />
            <span className="family-home-hub-copy">{card.desc}</span>
          </button>
        ))}
      </section>
      <p className="family-home-learning-intro">
        Academic Eye helps families understand what their child knows, what they are learning, and what comes next.
        Learning is a process, not a race, so we show progress throughout learning, not just a final grade at the end
        of a unit. Instead of relying on one score or percentage, Academic Eye looks at grade-level standards and
        multiple pieces of evidence over time to give families a clearer, more meaningful picture of student learning.
      </p>

      <GradingColorReference introduction="Use this reference when reviewing your child's learning progress in Grading." />

      {/* Messages Spot on Family Dashboard */}
      {messagingSummary && messagingSummary.incoming_count > 0 ? (
        <section
          aria-label="New Messages from Teachers"
          style={{
            ...panelStyle,
            background: '#f0f7ff',
            border: '1.5px solid #93c5fd',
            padding: '16px 20px',
            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.08)',
          }}
          data-testid="parent-dashboard-messages-spot"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#dbeafe', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                💬
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h2 style={{ fontSize: 16, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>
                    New Messages from Teachers
                  </h2>
                  <span style={{ fontSize: 11, fontWeight: 700, background: '#2563eb', color: '#ffffff', borderRadius: 999, padding: '2px 8px' }}>
                    {messagingSummary.incoming_count} {messagingSummary.incoming_count === 1 ? 'new' : 'new'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  Recent updates and messages from your child's teachers.
                </div>
              </div>
            </div>
            <button
              type="button"
              style={{ background: '#2563eb', color: '#ffffff', border: '1px solid #1d4ed8', borderRadius: 10, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              onClick={() => navigate('/parent/connect/messages')}
            >
              Open Messages →
            </button>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            {(messagingSummary.recent_messages || []).slice(0, 3).map((msg) => (
              <div
                key={msg.message_id}
                role="button"
                tabIndex={0}
                style={{
                  background: '#ffffff',
                  border: '1px solid #bfdbfe',
                  borderRadius: 10,
                  padding: '10px 14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'background 0.15s, border-color 0.15s',
                  gap: 12,
                }}
                onClick={() => navigate(`/parent/connect/messages?participant_id=${msg.sender_id}&student_id=${msg.student_id || ''}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(`/parent/connect/messages?participant_id=${msg.sender_id}&student_id=${msg.student_id || ''}`);
                  }
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>{msg.sender_name}</span>
                    {msg.student_name && (
                      <span style={{ fontSize: 11, background: '#e0f2fe', color: '#0369a1', padding: '1px 7px', borderRadius: 6, fontWeight: 600 }}>
                        {msg.student_name}
                      </span>
                    )}
                    {msg.sent_at && (
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>
                        {new Date(msg.sent_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}{' '}
                        {new Date(msg.sent_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    &ldquo;{msg.body}&rdquo;
                  </div>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', whiteSpace: 'nowrap' }}>
                  Reply →
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section aria-label="District and school information">
        <div style={{ ...panelStyle, padding: 18, display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#1e3a5f' }}>District & School Pages</div>
          {enrolledSchools.length === 0 ? (
            <div style={{ borderRadius: 14, background: '#f8fafc', border: '1px dashed #bed1ef', padding: '12px 14px', color: '#64748b', fontSize: 13, lineHeight: 1.6 }}>
              No enrolled school found yet for linked students.
            </div>
          ) : enrolledSchools.map((brand) => (
            <div key={brand.name} style={{ borderRadius: 14, background: brand.surface, border: `1px solid ${brand.accent}33`, padding: '12px 14px', color: '#475569', fontSize: 13, lineHeight: 1.6, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: brand.accent, border: `1px solid ${brand.accent}40`, overflow: 'hidden', flex: '0 0 auto' }}>
                <img src={brand.logo} alt={brand.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit', display: 'block' }} />
              </div>
              <div>
                <div style={{ fontWeight: 800, color: '#1e3a5f', marginBottom: 2 }}>{brand.name}</div>
                <div>Use family pages with school-aware branding for a clearer district and campus experience.</div>
              </div>
            </div>
          ))}
          <div style={{ fontSize: 18, fontWeight: 800, color: '#1e3a5f', marginTop: 4 }}>Quick Reminders</div>
          {[
            'Open Grading to review Student Profiles, View Data, and reports for your child.',
            'Use Action for family action cards. Student Goals: Coming soon.',
            'Open Connect when you want to send or check messages.',
            'Use Help for system walkthroughs and navigation tutorials.',
          ].map((item) => (
            <div key={item} style={{ borderRadius: 14, background: '#f8fafc', border: '1px solid #dbe4f0', padding: '12px 14px', color: '#475569', fontSize: 13, lineHeight: 1.6 }}>
              {item}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
